// Lib
import React, { useState, useLayoutEffect, useRef } from 'react';
import 'cropperjs/dist/cropper.css';
import Cropper from 'cropperjs';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { inRange } from 'lodash/fp';

// Utils
import { getElementModalSize } from '../../../modal/elementModalUtils';
import { getFilenameFromUrl, appendToFilename } from '../imageModalFileUtils';
import {
    getCaption,
    getElementId,
    getFileMimeType,
    getHasDrawing,
    getImageProp,
    getShowCaption,
} from '../../../../../common/elements/utils/elementPropertyUtils';
import {
    getLargestImageSize,
    getOriginalImage,
    getImageEditorDataRotation,
    getOriginalImageWidth,
    getOriginalImageHeight,
} from '../imageModalUtils';
import { getImageSource } from '../../imageHelper';

// Actions
import { uploadImage } from '../../../attachments/attachmentActions';
import { updateElement } from '../../../actions/elementActions';

// Selectors
import { getIsFeatureEnabledForCurrentUser } from '../../../feature/elementFeatureSelector';

// Components
import Icon from '../../../../components/icons/Icon';
import Button from '../../../../components/buttons/Button';
import Spinner from '../../../../components/loaders/Spinner';
import ElementImage from '../../../../components/images/ElementImage';
import Caption from '../../../../components/caption/Caption';
import { SimpleDrawingSvg } from '../../../drawing/DrawingSvg';
import ImageModalToolbarSecondaryTools from '../toolbar/ImageModalToolbarSecondaryTools';
import { ImMNElement } from '../../../../../common/elements/elementModelTypes';

// Constants
import { IMAGE_MODAL_FOOTER_SIZE } from '../imageModalConstants';
import { IMAGE_TYPES } from '../../../../../common/media/mediaConstants';
import { ExperimentId } from '../../../../../common/experiments/experimentsConstants';

// Styles
import './ImageModalEditor.scss';

// Hooks
import { useImageRotation } from './hooks/useImageRotation';
import { useCropSaveHandler } from './hooks/useCropSaveHandler';
import {
    fetchImageUsingCapacitorHTTP,
    loadCanvasIntoCropper,
    loadImageIntoCanvas,
    adjustCropperForRotation,
} from './utils/cropperUtils';

import globalLogger from '../../../../logger';
import { useTranslator } from '../../../../components/i18n/useTranslator';

const logger = globalLogger.createChannel('image.modal.editor');

interface ImageModalEditorProps {
    element: ImMNElement;
    gridSize: number;
    attachment: any;
    windowWidth: number;
    windowHeight: number;
    idealHeight: number;
    cancelEditing: () => void;
    afterSaveCb: () => void;
    uploadEditedImage: (elementId: string, file: Blob) => void;
    updateElementImageData: (data: {
        elementId: string;
        imageOriginal: any;
        imageEditorData: any;
        clearDrawing: boolean;
    }) => void;
    revertToOriginalImageData: (data: { elementId: string; imageOriginal: any; clearDrawing: boolean }) => void;
    renderSecureImagesThroughCF: boolean;
}

const ImageModalEditor = (props: ImageModalEditorProps) => {
    const dispatch = useDispatch();
    const { element, gridSize, attachment, windowWidth, windowHeight, idealHeight, cancelEditing, afterSaveCb } = props;

    const translate = useTranslator('popup', 'imageModal.editor');
    const useSecureMediaUrl = true;

    const uploadEditedImage = (elementId: string, file: Blob) =>
        dispatch(
            uploadImage({
                id: elementId,
                file,
                attachmentData: undefined,
                imageType: IMAGE_TYPES.ELEMENT,
            }),
        );
    const updateElementImageData = (
        elementId: string,
        imageOriginal: any,
        imageEditorData: any,
        clearDrawing: boolean,
    ) => {
        const update = {
            id: elementId,
            changes: {
                imageOriginal,
                imageEditorData,
                file: null,
                // Remove media param here. That the where the crop information for
                // command-drag aspect-ratio-free crops are saved.
                // So once it's "really" cropped, we can discard
                media: null,
                drawing: clearDrawing ? null : undefined,
                hasDrawing: clearDrawing ? false : undefined,
            },
            silent: true,
            sync: true,
        };

        return dispatch(updateElement(update));
    };
    const revertToOriginalImageData = (elementId: string, imageOriginal: any, clearDrawing: boolean) => {
        const update = {
            id: elementId,
            changes: {
                image: imageOriginal,
                imageOriginal: null,
                imageEditorData: null,
                file: null,
                drawing: clearDrawing ? null : undefined,
                hasDrawing: clearDrawing ? false : undefined,
            },
            silent: true,
            sync: true,
        };

        return dispatch(updateElement(update));
    };
    const renderSecureImagesThroughCF = useSelector(
        getIsFeatureEnabledForCurrentUser(ExperimentId.renderSecureImagesThroughCF),
    );

    const [initialRotation] = useState(getImageEditorDataRotation(element) || 0);
    const [currentRotation, setCurrentRotation] = useState(getImageEditorDataRotation(element) || 0);
    const [hideDrawing, setHideDrawing] = useState(false);
    const [showWarningDialog, setShowWarningDialog] = useState(false);

    const imageRef = useRef<HTMLImageElement | null>(null);
    const cropperRef = useRef<Cropper | null>(null);
    const isDirtyRef = useRef(false);
    const initialCropDataRef = useRef<Cropper.CropBoxData | null>();
    const [editorIsReady, setEditorIsReady] = useState(false);

    const elementId = getElementId(element);
    const hasDrawing = getHasDrawing(element);
    const imageType = getFileMimeType(element) || 'image/jpeg';
    const caption = getShowCaption(element) && getCaption(element);

    const originalImageWidth = getOriginalImageWidth({ element, gridSize, attachment });
    const originalImageHeight = getOriginalImageHeight({ element, gridSize, attachment });

    const isRotatedOnSide = !!currentRotation && currentRotation !== -180;

    // Flip the image width and height if it's rotated onto its side
    const imageWidth = isRotatedOnSide ? originalImageHeight : originalImageWidth;
    const imageHeight = isRotatedOnSide ? originalImageWidth : originalImageHeight;

    const { modalWidth, modalHeight } = getElementModalSize({
        windowWidth,
        windowHeight,
        imageWidth,
        imageHeight,
        idealHeight,
        caption,
        footerSize: IMAGE_MODAL_FOOTER_SIZE,
        modalMinWidth: 0,
        modalMinHeight: 0,
        gridSize,
        modalVerticalMarginGU: 5,
    });

    const svgWrapperStyle = {
        width: '100%',
        height: '100%',
    };

    const loadingImageStyles = {
        width: isRotatedOnSide ? modalHeight : modalWidth,
        height: isRotatedOnSide ? modalWidth : modalHeight,
        transform: `rotate(${initialRotation || 0}deg)`,
    };

    const imageDetails = getOriginalImage(element);
    const imageSize = getLargestImageSize(imageDetails);
    const largestUneditedImageUrl = getImageSource({
        imageDetails,
        imageSize,
        elementId,
        useSecureMediaUrl,
        renderSecureImagesThroughCF,
    });

    const { handleRotateImage } = useImageRotation({
        cropperRef,
        isDirtyRef,
        initialCropDataRef,
        currentRotation,
        initialRotation,
        setCurrentRotation,
        setHideDrawing,
    });
    const { handleSave } = useCropSaveHandler({
        cropperRef,
        isDirtyRef,
        element,
        elementId,
        imageWidth,
        imageHeight,
        imageType,
        largestUneditedImageUrl,
        currentRotation,
        hideDrawing,
        afterSaveCb,
        updateElementImageData,
        revertToOriginalImageData,
        uploadEditedImage,
        getOriginalImage,
        getImageProp,
        getFilenameFromUrl,
        appendToFilename,
        inRange,
    });

    useLayoutEffect(() => {
        if (cropperRef.current) return;
        if (!imageRef.current || !largestUneditedImageUrl) return;

        let objectUrl: string | null = null;

        const fetchAndLoadImage = async () => {
            try {
                // In order to avoid any CORS issues with getting data via capacitor, we are
                // using Capacitors HTTP library to download the image data and then initialising
                // the Image and Cropper elements manually rather than passing in a URL to src
                // directly
                const { objectUrl: url } = await fetchImageUsingCapacitorHTTP(largestUneditedImageUrl, imageType);
                objectUrl = url;

                const { dataUrl } = await loadImageIntoCanvas(objectUrl, imageType);

                if (imageRef.current instanceof HTMLImageElement) {
                    imageRef.current.src = dataUrl;

                    loadCanvasIntoCropper(imageRef, cropperRef, element, setEditorIsReady, isDirtyRef);
                }
            } catch (error) {
                // Sensitive data audit: SAFE — warn/error auto-sanitized (image file names/URLs redacted, error normalized)
                logger.error('Failed to load image for editing', error);
            }
        };

        fetchAndLoadImage();

        return () => {
            if (cropperRef.current) {
                cropperRef.current.destroy();
            }
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, []);

    // In order to rotate the cropper instance, we need to wait until the container element is re-rendered,
    // then render the cropper and re-set the crop rectangle
    useLayoutEffect(() => {
        const cropper = cropperRef.current;
        if (!editorIsReady || !cropper) return;

        const initialCropData = initialCropDataRef.current || cropper.getCropBoxData();

        adjustCropperForRotation(cropper, modalWidth, initialCropData);
    }, [currentRotation]);

    const onRotateLeftClick = () => {
        if (currentRotation === initialRotation && hasDrawing) return setShowWarningDialog(true);
        return handleRotateImage();
    };

    const handleContinueRotation = () => {
        setShowWarningDialog(false);
        handleRotateImage();
    };

    const handleCancelRotation = () => {
        setShowWarningDialog(false);
    };

    return (
        <>
            <div className="image-modal-content">
                <div className="image-container">
                    <div className="ImageEditor">
                        <div
                            className={classNames('editor', { loading: !editorIsReady })}
                            style={{ width: modalWidth, height: modalHeight }}
                        >
                            {!editorIsReady && (
                                <div className="loading-message">
                                    <div className="loading-image" style={loadingImageStyles}>
                                        <ElementImage
                                            useSecureMediaUrl={useSecureMediaUrl}
                                            showBrokenIconOnError
                                            element={element}
                                            imageData={getOriginalImage(element)}
                                            imageType={IMAGE_TYPES.ELEMENT}
                                            widthPx={modalWidth}
                                            key={elementId}
                                        />
                                    </div>
                                    <SimpleDrawingSvg element={element} />
                                    <div className="loading-overlay">
                                        <Spinner show />
                                        {translate('loadingEditor')}…
                                    </div>
                                </div>
                            )}

                            <img
                                ref={(c) => {
                                    imageRef.current = c;
                                }}
                                className="editor-source-image"
                                style={{ width: modalWidth, height: modalHeight }}
                            />
                            {hasDrawing && !hideDrawing && editorIsReady && (
                                <div className="drawing-svg-container">
                                    <div className="drawing-wrapper" style={svgWrapperStyle}>
                                        <SimpleDrawingSvg element={element} />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <Caption
                        element={element}
                        textContent={getCaption(element)}
                        captionVisible={getShowCaption(element)}
                    />
                    {showWarningDialog && (
                        <div className="warning-dialog">
                            <div className="dialog-body">
                                <div className="dialog-text">
                                    <span className="warning-label">{translate('warningLabel')}</span>
                                    &nbsp;
                                    {translate('warningMessage')}
                                </div>

                                <div className="buttons">
                                    <Button className="StyledButton secondary" onClickFn={handleCancelRotation}>
                                        {translate('cancel')}
                                    </Button>
                                    <Button className="StyledButton danger" onClickFn={handleContinueRotation}>
                                        {translate('continueAnyway')}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <ImageModalToolbarSecondaryTools className="ImageEditorModalToolbar">
                <Button
                    className="StyledButton secondary image-control rotate"
                    onClickFn={onRotateLeftClick}
                    disabled={!editorIsReady || showWarningDialog}
                >
                    <Icon name="modal-toolbar-rotate" />
                    {translate('rotateLeft')}
                </Button>

                <Button className="StyledButton secondary cancel no-hover" onClickFn={cancelEditing}>
                    {translate('cancel')}
                </Button>

                <Button
                    className="StyledButton primary save"
                    onClickFn={handleSave}
                    disabled={!editorIsReady || showWarningDialog}
                >
                    {translate('save')}
                </Button>
            </ImageModalToolbarSecondaryTools>
        </>
    );
};

export default ImageModalEditor;
